import { useMemo, useRef, useState } from 'react'
import { AlertTriangle, ArrowLeft, ArrowRight, Check, FileSpreadsheet, Upload } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCommitImport, useImportSchema, usePreviewImport } from '@/hooks/useInventoryMaster'
import {
  applyRemembered,
  decisionKey,
  defaultNames,
  describeValues,
  parseImportFile,
  rememberMappings,
  rememberedMappings,
  splitDecisions,
  targetColor,
  targetId,
  targetOptions,
  withDefaults,
  type DecisionChoice,
  type ImportCell,
  type TargetOption,
} from '@/lib/materialImport'
import { PROFILE_TYPE_COLOR } from '@/lib/inventoryProfiles'
import { InventoryImportError } from '@/api/inventoryApi'
import type {
  ImportDecision,
  ImportPlan,
  ImportProfileDecision,
  ImportRequest,
  ImportSchema,
  ImportResult,
  ImportTarget,
} from '@/api/inventoryApi'
import { useTenant } from '@/providers/TenantProvider'
import { cn } from '@/lib/utils'

type Step = 'upload' | 'map' | 'preview' | 'done'

const STEPS: { id: Step; label: string }[] = [
  { id: 'upload', label: 'Choose file' },
  { id: 'map', label: 'Match columns' },
  { id: 'preview', label: 'Check rows' },
  { id: 'done', label: 'Imported' },
]

interface MaterialImportWizardProps {
  open: boolean
  onClose: () => void
}

/**
 * Load a materials master from a spreadsheet.
 *
 * Four steps, because the file is a stranger: we do not know what its columns
 * mean, and the two things the importer cannot decide on its own — a column
 * that could be several things, and a set of values that could be a shared
 * profile or one material's own — are exactly what the user is here to answer.
 * Nothing is written until the last step.
 */
export function MaterialImportWizard({ open, onClose }: MaterialImportWizardProps) {
  const [step, setStep] = useState<Step>('upload')
  const [fileName, setFileName] = useState('')
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<ImportCell[][]>([])
  const [fileErrors, setFileErrors] = useState<string[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [decisions, setDecisions] = useState<Record<string, DecisionChoice>>({})
  const [newNames, setNewNames] = useState<Record<string, string>>({})
  const [plan, setPlan] = useState<ImportPlan | null>(null)
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [result, setResult] = useState<ImportResult | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const { data: schema } = useImportSchema(open)
  const { selectedTenantId } = useTenant()
  const preview = usePreviewImport()
  const commit = useCommitImport()

  const options = useMemo(() => (schema ? targetOptions(schema) : []), [schema])
  const optionById = useMemo(() => new Map(options.map((o) => [o.id, o])), [options])

  function reset() {
    setStep('upload')
    setFileName('')
    setHeaders([])
    setRows([])
    setFileErrors([])
    setMapping({})
    setDecisions({})
    setNewNames({})
    setPlan(null)
    setServerErrors([])
    setResult(null)
    preview.reset()
    commit.reset()
  }

  function close() {
    reset()
    onClose()
  }

  /**
   * Read a file into headers + rows.
   *
   * Deliberately does not close and reopen the wizard: correcting a file and
   * re-choosing it is the normal path after a failed preview, not an exception.
   */
  async function onFileChosen(file: File | undefined) {
    if (!file) return
    setServerErrors([])
    setPlan(null)
    const parsed = await parseImportFile(file)
    setFileName(file.name)
    setHeaders(parsed.headers)
    setRows(parsed.rows)
    setFileErrors(parsed.errors)
    setMapping({})
    setDecisions({})
    if (parsed.errors.length === 0) void runPreview(parsed.headers, parsed.rows, {}, 'map')
  }

  function requestBody(
    h: string[],
    r: ImportCell[][],
    m: Record<string, string>,
  ): ImportRequest {
    return {
      headers: h,
      rows: r.map((cells) => ({ cells })),
      mapping: Object.entries(m).flatMap(([header, optionId]): NonNullable<ImportRequest['mapping']> => {
        if (optionId === 'ignore') return [{ header, kind: 'ignore' as const }]
        const option = optionById.get(optionId)
        if (!option) return []
        const t = option.target
        if (t.kind === 'materialScalar') {
          return [{ header, kind: 'materialScalar' as const, key: t.key }]
        }
        if (t.kind === 'profileName') {
          return [{ header, kind: 'profileName' as const, profileType: t.profileType }]
        }
        return [{ header, kind: 'fieldValue' as const, profileType: t.profileType, key: t.key }]
      }),
    }
  }

  /**
   * Re-run the dry-run and move on only if it came back.
   *
   * `onDone` rather than a `.then()` at the call site: a failed preview has to
   * leave the user on the step they were already on, with the errors, and a
   * chained transition would advance them past it.
   */
  async function runPreview(
    h: string[],
    r: ImportCell[][],
    m: Record<string, string>,
    onDone: Step,
  ) {
    setServerErrors([])
    try {
      const next = await preview.mutateAsync(requestBody(h, r, m))
      setPlan(next)
      setDecisions((current) => withDefaults(next.decisions, current))
      setNewNames((current) => defaultNames(next.decisions, current))
      // Pre-fill anything this tenant mapped last time. A bare category label
      // names no field, so there is nothing to auto-match — but there is what
      // they chose for that same heading on the previous import.
      const recalled = applyRemembered(next.columns, rememberedMappings(selectedTenantId), options)
      if (Object.keys(recalled).length > 0) {
        setMapping((current) => ({ ...recalled, ...current }))
      }
      setStep(onDone)
    } catch (err) {
      setServerErrors(errorsFrom(err))
    }
  }

  const ambiguous = plan?.columns.filter((c) => c.status === 'ambiguous') ?? []
  const unmatched = plan?.columns.filter((c) => c.status === 'unmatched') ?? []
  const rowErrors = plan?.errors ?? []

  const undecided = (plan?.decisions ?? []).filter((d) => {
    const answer = decisions[decisionKey(d.profileType, d.valueSetKey)] ?? ''
    if (answer === '') return true
    if (answer === 'create') return !newNames[decisionKey(d.profileType, d.valueSetKey)]?.trim()
    return false
  })

  const canCommit =
    !!plan && ambiguous.length === 0 && rowErrors.length === 0 && undecided.length === 0

  async function runCommit() {
    if (!plan) return
    const payload: ImportDecision[] = plan.decisions.map((d) => {
      const key = decisionKey(d.profileType, d.valueSetKey)
      const answer = decisions[key] ?? ''
      if (answer.startsWith('reuse:')) {
        return {
          profileType: d.profileType,
          valueSetKey: d.valueSetKey,
          choice: 'reuse',
          profileId: answer.slice('reuse:'.length),
        }
      }
      if (answer === 'create') {
        return {
          profileType: d.profileType,
          valueSetKey: d.valueSetKey,
          choice: 'create',
          name: newNames[key]?.trim(),
        }
      }
      return { profileType: d.profileType, valueSetKey: d.valueSetKey, choice: 'attributes' }
    })

    setServerErrors([])
    try {
      setResult(await commit.mutateAsync({ ...requestBody(headers, rows, mapping), decisions: payload }))
      // Only after a successful import: a mapping that produced nothing is not
      // one worth offering back next time.
      rememberMappings(selectedTenantId, mapping)
      setStep('done')
    } catch (err) {
      setServerErrors(errorsFrom(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-4xl'>
        <DialogHeader>
          <DialogTitle className='text-lg'>Import materials</DialogTitle>
        </DialogHeader>

        <StepRail current={step} />

        {step === 'upload' && (
          <UploadStep
            fileName={fileName}
            errors={[...fileErrors, ...serverErrors]}
            busy={preview.isPending}
            onPick={() => fileInput.current?.click()}
          />
        )}

        {step === 'map' && plan && schema && (
          <MapStep
            plan={plan}
            options={options}
            mapping={mapping}
            onMap={(header, optionId) => setMapping((m) => ({ ...m, [header]: optionId }))}
            onSkipUnmatched={() =>
              setMapping((m) => ({
                ...Object.fromEntries(unmatched.map((c) => [c.header, 'ignore'])),
                ...m,
              }))
            }
            ambiguous={ambiguous.length}
            unmatched={unmatched.filter((c) => !mapping[c.header]).length}
          />
        )}

        {step === 'preview' && plan && (
          <PreviewStep
            plan={plan}
            decisions={decisions}
            newNames={newNames}
            schema={schema}
            onDecide={(key, choice) => setDecisions((d) => ({ ...d, [key]: choice }))}
            onName={(key, name) => setNewNames((n) => ({ ...n, [key]: name }))}
            serverErrors={serverErrors}
          />
        )}

        {step === 'done' && result && <DoneStep result={result} />}

        <input
          ref={fileInput}
          type='file'
          accept='.xlsx,.csv'
          className='hidden'
          onChange={(e) => {
            void onFileChosen(e.target.files?.[0])
            // Clear the input so choosing the SAME corrected file again still
            // fires a change event.
            e.target.value = ''
          }}
        />

        <div className='flex items-center justify-between gap-3 border-t pt-4'>
          <div className='text-sm text-muted-foreground'>
            {fileName && step !== 'done' && (
              <span className='inline-flex items-center gap-1.5'>
                <FileSpreadsheet className='h-4 w-4' />
                {fileName} · {rows.length} row{rows.length === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className='flex gap-2'>
            {step === 'map' && (
              <>
                <Button variant='outline' onClick={() => setStep('upload')}>
                  <ArrowLeft className='mr-1 h-4 w-4' />
                  Choose another file
                </Button>
                <Button
                  disabled={ambiguous.length > 0 || preview.isPending}
                  onClick={() => void runPreview(headers, rows, mapping, 'preview')}
                >
                  Check rows
                  <ArrowRight className='ml-1 h-4 w-4' />
                </Button>
              </>
            )}
            {step === 'preview' && (
              <>
                <Button variant='outline' onClick={() => setStep('map')}>
                  <ArrowLeft className='mr-1 h-4 w-4' />
                  Back to columns
                </Button>
                <Button disabled={!canCommit || commit.isPending} onClick={() => void runCommit()}>
                  {commit.isPending
                    ? 'Importing…'
                    : `Import ${plan?.summary.rows ?? 0} material${plan?.summary.rows === 1 ? '' : 's'}`}
                </Button>
              </>
            )}
            {step === 'done' ? (
              <Button onClick={close}>Done</Button>
            ) : (
              <Button variant='ghost' onClick={close}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── Steps ───────────────────────────────────────────────────────────────────

function StepRail({ current }: { current: Step }) {
  const index = STEPS.findIndex((s) => s.id === current)
  return (
    <ol className='flex items-center gap-2 text-sm'>
      {STEPS.map((s, i) => (
        <li key={s.id} className='flex items-center gap-2'>
          <span
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
              i < index && 'bg-emerald-100 text-emerald-700',
              i === index && 'bg-primary text-primary-foreground',
              i > index && 'bg-gray-100 text-gray-400',
            )}
          >
            {i < index ? <Check className='h-3.5 w-3.5' /> : i + 1}
          </span>
          <span className={cn(i === index ? 'font-medium text-gray-900' : 'text-gray-500')}>
            {s.label}
          </span>
          {i < STEPS.length - 1 && <span className='mx-1 h-px w-6 bg-gray-200' />}
        </li>
      ))}
    </ol>
  )
}

function UploadStep({
  fileName,
  errors,
  busy,
  onPick,
}: {
  fileName: string
  errors: string[]
  busy: boolean
  onPick: () => void
}) {
  return (
    <div className='space-y-4 py-4'>
      <button
        type='button'
        onClick={onPick}
        disabled={busy}
        className='flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-gray-200 px-6 py-10 text-center transition-colors hover:border-primary hover:bg-gray-50 disabled:opacity-60'
      >
        <Upload className='h-7 w-7 text-gray-400' />
        <span className='font-medium text-gray-900'>
          {busy ? 'Reading the file…' : fileName || 'Choose a .xlsx or .csv file'}
        </span>
        <span className='text-sm text-muted-foreground'>
          The first row must be the column headings. Up to 500 materials at a time.
        </span>
      </button>
      <ErrorList title='This file cannot be read yet' errors={errors} />
    </div>
  )
}

function MapStep({
  plan,
  options,
  mapping,
  onMap,
  onSkipUnmatched,
  ambiguous,
  unmatched,
}: {
  plan: ImportPlan
  options: TargetOption[]
  mapping: Record<string, string>
  onMap: (header: string, optionId: string) => void
  onSkipUnmatched: () => void
  ambiguous: number
  unmatched: number
}) {
  return (
    <div className='space-y-4 py-2'>
      <p className='text-sm text-muted-foreground'>
        {ambiguous > 0
          ? `${ambiguous} column${ambiguous === 1 ? '' : 's'} could mean more than one thing. Pick which, and the rest are ready.`
          : unmatched > 0
            ? `Everything is matched except ${unmatched} column${unmatched === 1 ? '' : 's'} we have no field for. Map or skip them, or leave them — they will be ignored.`
            : 'Every column matched a field. Check them over and carry on.'}
      </p>

      {unmatched > 0 && (
        <Button variant='outline' size='sm' onClick={onSkipUnmatched}>
          Skip the {unmatched} unmatched column{unmatched === 1 ? '' : 's'}
        </Button>
      )}

      <div className='overflow-hidden rounded-lg border'>
        <table className='w-full text-sm'>
          <thead className='bg-gray-50 text-left text-xs text-gray-500'>
            <tr>
              <th className='px-3 py-2 font-medium'>Column in your file</th>
              <th className='px-3 py-2 font-medium'>Goes to</th>
            </tr>
          </thead>
          <tbody className='divide-y'>
            {plan.columns.map((column) => {
              const needsAnswer = column.status === 'ambiguous'
              const current =
                mapping[column.header] ?? (column.target ? targetId(column.target) : '')
              return (
                <tr key={`${column.header}-${column.index}`} className={needsAnswer ? 'bg-amber-50/60' : ''}>
                  <td className='px-3 py-2 align-middle'>
                    <div className='font-medium text-gray-900'>{column.header || '(no heading)'}</div>
                    {column.note && (
                      <div
                        className={cn(
                          'text-xs',
                          needsAnswer ? 'text-amber-700' : 'text-muted-foreground',
                        )}
                      >
                        {column.note}
                      </div>
                    )}
                  </td>
                  <td className='px-3 py-2 align-middle'>
                    <div className='flex items-center gap-2'>
                      <Select value={current} onValueChange={(v) => onMap(column.header, v)}>
                        <SelectTrigger
                          className={cn('h-9 w-[22rem]', needsAnswer && 'border-amber-400')}
                        >
                          <SelectValue placeholder='Pick a field…' />
                        </SelectTrigger>
                        <SelectContent className='max-h-72'>
                          <SelectItem value='ignore'>Skip this column</SelectItem>
                          {options.map((o) => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {current && current !== 'ignore' && (
                        <ColorDot target={options.find((o) => o.id === current)?.target} />
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PreviewStep({
  plan,
  decisions,
  newNames,
  schema,
  onDecide,
  onName,
  serverErrors,
}: {
  plan: ImportPlan
  decisions: Record<string, DecisionChoice>
  newNames: Record<string, string>
  /** Resolves field keys to their labels for the decision cards. */
  schema: ImportSchema | undefined
  onDecide: (key: string, choice: DecisionChoice) => void
  onName: (key: string, name: string) => void
  serverErrors: string[]
}) {
  const [showPerRow, setShowPerRow] = useState(false)

  // A value-set carried by several materials looks like a shared profile and is
  // worth a decision. One carried by a single material is that material's own
  // data — pre-answered, and folded away unless the user asks to see it.
  const { shared, perRow } = splitDecisions(plan.decisions)

  return (
    <div className='space-y-5 py-2'>
      <div className='flex flex-wrap gap-2 text-sm'>
        <Stat label='new' value={plan.summary.create} tone='emerald' />
        <Stat label='updated' value={plan.summary.update} tone='sky' />
        {shared.length > 0 && (
          <Stat label='shared value sets' value={shared.length} tone='violet' />
        )}
      </div>

      {shared.length > 0 && (
        <section className='space-y-3'>
          <h3 className='text-sm font-semibold text-gray-900'>
            These values repeat across materials. Make them a shared profile?
          </h3>
          {shared.map((d) => {
            const key = decisionKey(d.profileType, d.valueSetKey)
            return (
              <DecisionRow
                key={key}
                decision={d}
                answer={decisions[key] ?? ''}
                name={newNames[key] ?? ''}
                schema={schema}
                onDecide={(c) => onDecide(key, c)}
                onName={(n) => onName(key, n)}
              />
            )
          })}
        </section>
      )}

      {perRow.length > 0 && (
        <section className='rounded-lg border border-dashed p-3'>
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <p className='text-sm text-gray-700'>
              <strong className='font-semibold'>{perRow.length}</strong> other value set
              {perRow.length === 1 ? '' : 's'} appear on one material each — kept on that material.
            </p>
            <Button variant='ghost' size='sm' onClick={() => setShowPerRow((v) => !v)}>
              {showPerRow ? 'Hide' : 'Review individually'}
            </Button>
          </div>
          {showPerRow && (
            <div className='mt-3 space-y-3'>
              {perRow.map((d) => {
                const key = decisionKey(d.profileType, d.valueSetKey)
                return (
                  <DecisionRow
                    key={key}
                    decision={d}
                    answer={decisions[key] ?? ''}
                    name={newNames[key] ?? ''}
                    schema={schema}
                    onDecide={(c) => onDecide(key, c)}
                    onName={(n) => onName(key, n)}
                  />
                )
              })}
            </div>
          )}
        </section>
      )}

      <section className='space-y-2'>
        <h3 className='text-sm font-semibold text-gray-900'>Rows</h3>
        <div className='max-h-64 overflow-y-auto rounded-lg border'>
          <table className='w-full text-sm'>
            <tbody className='divide-y'>
              {plan.rows.map((row) => (
                <tr
                  key={row.row}
                  className={row.errors.length || row.columnIssues.length ? 'bg-rose-50/60' : ''}
                >
                  <td className='w-16 px-3 py-1.5 text-xs text-muted-foreground'>Row {row.row}</td>
                  <td className='w-24 px-3 py-1.5'>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-medium',
                        row.action === 'create'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-sky-100 text-sky-700',
                      )}
                    >
                      {row.action === 'create' ? 'New' : 'Update'}
                    </span>
                  </td>
                  <td className='px-3 py-1.5'>
                    <span className='font-medium text-gray-900'>{row.materialCode ?? '—'}</span>
                    {row.errors.map((e) => (
                      <div key={e} className='text-xs text-rose-700'>
                        {e.replace(/^Row \d+: /, '')}
                      </div>
                    ))}
                    {/* Column problems are stated once above. Repeating them on
                        every row they touch is what buried the row-specific
                        ones, so the row only says how many it has. */}
                    {row.columnIssues.length > 0 && (
                      <div className='text-xs text-rose-700/70'>
                        {row.columnIssues.length} value
                        {row.columnIssues.length === 1 ? '' : 's'} refused — see above
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <ErrorList title='Nothing was written' errors={serverErrors} />
    </div>
  )
}

function DoneStep({ result }: { result: ImportResult }) {
  return (
    <div className='space-y-3 py-8 text-center'>
      <div className='mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100'>
        <Check className='h-6 w-6 text-emerald-700' />
      </div>
      <p className='text-lg font-medium text-gray-900'>
        {result.created} created, {result.updated} updated
      </p>
      {(result.profilesCreated > 0 || result.profilesReused > 0) && (
        <p className='text-sm text-muted-foreground'>
          {result.profilesCreated > 0 && `${result.profilesCreated} new profile${result.profilesCreated === 1 ? '' : 's'}`}
          {result.profilesCreated > 0 && result.profilesReused > 0 && ', '}
          {result.profilesReused > 0 && `${result.profilesReused} reused`}
        </p>
      )}
    </div>
  )
}

// ─── Small parts ─────────────────────────────────────────────────────────────

function DecisionRow({
  decision,
  answer,
  name,
  schema,
  onDecide,
  onName,
}: {
  decision: ImportProfileDecision
  answer: DecisionChoice
  name: string
  schema: ImportSchema | undefined
  onDecide: (choice: DecisionChoice) => void
  onName: (name: string) => void
}) {
  const described = describeValues(schema, decision.profileType, decision.values)
  return (
    <div className='rounded-lg border p-3'>
      <div className='flex flex-wrap items-center gap-2'>
        <span
          className={cn(
            'rounded-full border px-2 py-0.5 text-xs font-medium',
            PROFILE_TYPE_COLOR[decision.profileType].chip,
          )}
        >
          {decision.categoryLabel}
        </span>
        <span className='text-xs text-muted-foreground'>
          row{decision.rows.length === 1 ? '' : 's'} {formatRows(decision.rows)}
        </span>
      </div>

      {decision.missingProfileName ? (
        <p className='mt-2 text-sm text-gray-700'>
          Your file asks for a profile named{' '}
          <strong className='font-semibold'>“{decision.missingProfileName}”</strong>, which this
          tenant does not have yet. It will be created.
          {described.summary && (
            <span className='mt-1 block text-muted-foreground'>
              {described.summary}
              {described.hidden > 0 && ` · +${described.hidden} more`}
            </span>
          )}
        </p>
      ) : (
        <p className='mt-2 text-sm text-gray-700'>
          {described.summary}
          {described.hidden > 0 && (
            <span className='text-muted-foreground'> · +{described.hidden} more</span>
          )}
        </p>
      )}

      <div className='mt-3 flex flex-wrap items-center gap-2'>
        <Select value={answer} onValueChange={(v) => onDecide(v as DecisionChoice)}>
          <SelectTrigger className='h-9 w-[20rem]'>
            <SelectValue placeholder='Choose what to do…' />
          </SelectTrigger>
          <SelectContent>
            {decision.suggestedProfileId && (
              <SelectItem value={`reuse:${decision.suggestedProfileId}`}>
                Use the existing “{decision.suggestedProfileName}” profile
              </SelectItem>
            )}
            <SelectItem value='create'>
              {decision.missingProfileName
                ? `Create “${decision.missingProfileName}”`
                : 'Create a new named profile'}
            </SelectItem>
            <SelectItem value='attributes'>
              {decision.missingProfileName
                ? 'Leave these materials without this profile'
                : 'Keep these values on each material'}
            </SelectItem>
          </SelectContent>
        </Select>

        {answer === 'create' && (
          <Input
            className='h-9 w-64'
            placeholder='Name the new profile'
            value={name}
            onChange={(e) => onName(e.target.value)}
          />
        )}
      </div>
    </div>
  )
}

function ColorDot({ target }: { target: ImportTarget | undefined }) {
  return <span className={cn('h-4 w-4 shrink-0 rounded-full border', targetColor(target))} />
}

// Written out rather than interpolated: Tailwind scans source text for whole
// class names, so a composed `bg-${tone}-50` is never generated and the stat
// renders unstyled.
const STAT_TONES = {
  emerald: 'bg-emerald-50 text-emerald-700',
  sky: 'bg-sky-50 text-sky-700',
  violet: 'bg-violet-50 text-violet-700',
} as const

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: keyof typeof STAT_TONES
}) {
  return (
    <span className={cn('rounded-full px-3 py-1', STAT_TONES[tone])}>
      <strong className='font-semibold'>{value}</strong> {label}
    </span>
  )
}

function ErrorList({ title, errors }: { title: string; errors: string[] }) {
  if (errors.length === 0) return null
  return (
    <div className='rounded-lg border border-rose-200 bg-rose-50 p-3'>
      <p className='flex items-center gap-1.5 text-sm font-medium text-rose-800'>
        <AlertTriangle className='h-4 w-4' />
        {title}
      </p>
      <ul className='mt-1.5 max-h-40 space-y-0.5 overflow-y-auto text-sm text-rose-700'>
        {errors.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** "2, 3 and 4", or "2, 3 and 12 more" once the list stops being readable. */
function formatRows(rows: number[]): string {
  if (rows.length <= 3) return rows.join(', ')
  return `${rows.slice(0, 2).join(', ')} and ${rows.length - 2} more`
}

/** Every problem the server reported, or the one message it managed. */
function errorsFrom(err: unknown): string[] {
  if (err instanceof InventoryImportError) {
    return err.errors.length > 0 ? err.errors : [err.message]
  }
  return [err instanceof Error ? err.message : 'The import could not be checked.']
}
